import { t } from 'i18next';
import {
  MESSAGE_LOCATION,
  MessageLocation,
  MessageReaction,
  MessageSeenBy,
  MessageType,
  SessionType,
} from 'models/Message';
import { REACTION_TYPE, ReactionType } from 'models/Reactions';
import { User } from 'models/User';
import { Lead } from 'models/Lead';
import utils, {
  getArrayLength,
  getHasElementsInArray,
  getIsArrayEmpty,
  getIsCurrentUser,
  getJoinedWordsWithComma,
  getUserFullNameOrEmail,
} from 'utils';

export const setDatesToMessages = (messages: Array<MessageType>) => {
  return messages.map((msg, index, array) => {
    let showDate: string;

    const currentDate = utils.toDateFormat(msg?.createdAt);
    const nextDate = utils.toDateFormat(
      index < array.length - 1 && array[index + 1]?.createdAt,
    );

    if (
      index === array.length - 1 ||
      (index === 0 && currentDate !== nextDate) ||
      currentDate !== nextDate
    )
      showDate = currentDate;
    else showDate = null;

    return {
      ...msg,
      showDate,
    } as MessageType;
  });
};

/**
 * Builds the "seen" label for a message from its seenBy entries.
 *
 * Sell-side viewers collapse into a single token — the company name when it
 * exists, otherwise "sell side". Buyers are listed individually by full name.
 * The current user's own entry is ignored (you've obviously seen your message).
 * When the sell side and every buy representative have seen it, returns
 * "Seen by all".
 */
export const getHasBeenSeen = (seenBy: MessageSeenBy[]) =>
  (seenBy ?? []).some((entry) => !getIsCurrentUser(entry.userId));

const getOtherViewers = (seenBy: MessageSeenBy[]) =>
  (seenBy ?? []).filter(({ userId }) => !getIsCurrentUser(userId));

/**
 * True when the sell side and every buy representative have seen the message.
 * The current user's own side always counts as seen — you don't appear in your
 * own `seenBy`, so a sell-side viewer satisfies the sell side and a buyer viewer
 * satisfies their own buyer slot. Direct messages have a single counterpart —
 * the interlocutor — so it's "all seen" as soon as anyone else has seen it.
 */
export const getHaveAllSeen = (
  seenBy: MessageSeenBy[],
  session: Pick<
    SessionType,
    'buyRepresentatives' | 'isDirectMessage' | 'isRepresentingSellSide'
  >,
) => {
  if (session?.isDirectMessage)
    return getHasElementsInArray(getOtherViewers(seenBy));

  const buyRepresentatives = session?.buyRepresentatives ?? [];

  const hasSellSeen =
    !!session?.isRepresentingSellSide ||
    getOtherViewers(seenBy).some(({ isSellSide }) => isSellSide);

  const haveAllBuyersSeen =
    getHasElementsInArray(buyRepresentatives) &&
    buyRepresentatives.every(
      (buyer) =>
        getIsCurrentUser(buyer._id) ||
        seenBy?.some(({ userId }) => userId === buyer._id),
    );

  return hasSellSeen && haveAllBuyersSeen;
};

export const getSeenLabel = (
  seenBy: MessageSeenBy[],
  session: Pick<
    SessionType,
    | 'sellRepresentative'
    | 'buyRepresentatives'
    | 'isDirectMessage'
    | 'interlocutor'
    | 'isRepresentingSellSide'
  >,
): string => {
  const sellCompanyName = session?.sellRepresentative?.companyName;
  const buyRepresentatives = session?.buyRepresentatives ?? [];

  const otherViewers = getOtherViewers(seenBy);

  if (getIsArrayEmpty(otherViewers)) return t('notSeenYet');

  if (session?.isDirectMessage) {
    const interlocutorName = getUserFullNameOrEmail(session.interlocutor);

    return interlocutorName
      ? t('seenByNames', { names: interlocutorName })
      : t('seenByAll');
  }

  const hasSellSeen = otherViewers.some(({ isSellSide }) => isSellSide);

  const sellToken = hasSellSeen ? [sellCompanyName || t('sellSideToken')] : [];

  const buyerTokens = otherViewers
    .filter(({ isSellSide }) => !isSellSide)
    .map(({ userId }) => buyRepresentatives.find(({ _id }) => _id === userId))
    .filter(Boolean)
    .map(getUserFullNameOrEmail);

  const names = [...sellToken, ...buyerTokens];

  if (getIsArrayEmpty(names)) return t('notSeenYet');

  // "Seen by all" only reads well for more than one viewer — a single reader is
  // named even when they're the only counterpart and the ticks already show all
  // seen.
  if (getArrayLength(names) > 1 && getHaveAllSeen(seenBy, session))
    return t('seenByAll');

  return t('seenByNames', { names: getJoinedWordsWithComma(names) });
};

/**
 * The current user's own reaction on a message, used to highlight their pick
 * in the reaction picker. Undefined when they haven't reacted.
 */
export const getActiveReaction = (reactions: MessageReaction[]) =>
  (reactions ?? []).find(({ userId }) => getIsCurrentUser(userId))?.reaction;

/**
 * Groups a message's reactions into the per-type user lists the reaction
 * components consume. Buyers resolve to their individual names from the
 * session; sell-side reactors collapse into a single company token — the same
 * constraint as the "seen" label, since the session never carries individual
 * sell-side users.
 *
 * Direct-message sessions have no buy/sell representatives — the only two
 * participants are the current user and the interlocutor — so reactors resolve
 * against those instead.
 */
export const getReactionUsers = (
  reactions: MessageReaction[],
  session: Pick<
    SessionType,
    | 'sellRepresentative'
    | 'buyRepresentatives'
    | 'isDirectMessage'
    | 'interlocutor'
  >,
  currentUser?: Partial<User>,
) => {
  const { isDirectMessage, interlocutor } = session ?? {};

  const sellCompanyName = session?.sellRepresentative?.companyName;
  const buyRepresentatives = session?.buyRepresentatives ?? [];

  const resolveReactor = (userId: string) =>
    isDirectMessage
      ? getIsCurrentUser(userId)
        ? currentUser
        : interlocutor
      : buyRepresentatives.find(({ _id }) => _id === userId);

  const buildUsers = (reactionType: ReactionType) => {
    const reactors = (reactions ?? []).filter(
      ({ reaction }) => reaction === reactionType,
    );

    const hasSellReactor =
      !isDirectMessage && reactors.some(({ isSellSide }) => isSellSide);

    const sellToken = hasSellReactor
      ? [{ _id: 'sell-side', firstName: sellCompanyName || t('sellSideToken') }]
      : [];

    const buyerUsers = reactors
      .filter(({ isSellSide }) => isDirectMessage || !isSellSide)
      .map(({ userId }) => resolveReactor(userId))
      .filter(Boolean);

    return [...sellToken, ...buyerUsers];
  };

  return {
    thumbsUp: buildUsers(REACTION_TYPE.THUMBS_UP),
    thumbsDown: buildUsers(REACTION_TYPE.THUMBS_DOWN),
    questionMark: buildUsers(REACTION_TYPE.QUESTION_MARK),
  };
};

export const getCanManageReleaseOrder = (
  session: SessionType,
  messageLocation: MessageLocation,
) =>
  messageLocation === MESSAGE_LOCATION.MESSAGING_PAGE &&
  !!session?.isRepresentingSellSide &&
  !session?.isGroupChat &&
  !!session?.leadId &&
  !session?.leadWithdrawnInterest;

export const getSessionLead = (session: SessionType) => {
  const { firstName, lastName, email } = session?.buyRepresentatives?.[0] || {};

  return { _id: session?.leadId, firstName, lastName, email } as Lead;
};
